
import { useCallback } from 'react';
import { AllowedStatus, BaseFormValues, WithServicesFormValues, WithoutServicesFormValues } from './SubscriptionFormTypes';
import { Subscription } from '@/lib/data';

export function createDefaultValues(
  initialCustomerId: string, 
  subscription: Subscription | undefined,
  availableServices: any[],
  showServices: boolean,
  defaultStatus: AllowedStatus
) {
  const baseValues: Partial<BaseFormValues> = {
    customer_id: initialCustomerId,
    installation_street: subscription?.installation_street || '',
    installation_zip_code: subscription?.installation_zip_code || '',
    installation_city: subscription?.installation_city || '',
    housing_type: subscription?.housing_type || 'house',
    activation_date: subscription?.activation_date ? new Date(subscription.activation_date) : null,
    status: (subscription?.status as AllowedStatus) || defaultStatus,
    alarm_com_id: subscription?.alarm_com_id || '',
    contact_name: subscription?.contact_name || '',
    contact_phone: subscription?.contact_phone || '',
    contact_email: subscription?.contact_email || '',
    prom_avarn: subscription?.prom_avarn || '',
    invoice_reference: subscription?.invoice_reference || '',
  };
  
  // Conditional type for form values with services
  if (showServices) {
    const servicesValues = availableServices.map(service => ({
      service_id: service.service_id,
      price: service.default_price,
      selected: false
    }));
    
    const withServicesValues = {
      ...baseValues,
      services: servicesValues
    };
    
    return withServicesValues;
  }
  
  return baseValues;
}

export function useSubscriptionFormHelpers(form: any, showServices: boolean) {
  // Update form services based on selection
  const updateFormServices = useCallback((updatedSelection: Record<string, boolean>) => {
    if (showServices) {
      const formValues = form.getValues();
      
      if (formValues.services) {
        const servicesData = formValues.services.map((service: any) => {
          if (service.service_id in updatedSelection) {
            return { ...service, selected: updatedSelection[service.service_id] };
          }
          return service;
        });
        
        form.setValue('services', servicesData);
      }
    }
  }, [form, showServices]);
  
  // Handle service selection changes
  const handleServiceSelectionChange = useCallback((
    service: any, 
    selected: boolean, 
    handleServiceSelection: (service: any, selected: boolean) => Record<string, boolean>
  ) => {
    const updatedSelection = handleServiceSelection(service, selected);
    updateFormServices(updatedSelection);
  }, [updateFormServices]);
  
  // Handle price changes for services
  const handleServicePriceChange = useCallback((
    serviceId: string, 
    value: string, 
    handlePriceChange: (serviceId: string, value: string) => Record<string, number | null>
  ) => {
    const updatedPrices = handlePriceChange(serviceId, value);
    
    if (showServices) {
      const formValues = form.getValues();
      
      if (formValues.services) {
        const servicesData = formValues.services.map((service: any) => {
          if (service.service_id === serviceId) {
            // Handle empty value case
            if (value === '' || updatedPrices[serviceId] === null) {
              return { ...service, price: null };
            } else {
              return { 
                ...service, 
                price: updatedPrices[serviceId] !== undefined ? updatedPrices[serviceId] : service.price 
              };
            }
          }
          return service;
        });
        
        form.setValue('services', servicesData);
      }
    }
  }, [form, showServices]);

  return {
    updateFormServices,
    handleServiceSelectionChange,
    handleServicePriceChange
  };
}
